import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { apiPrimeSlice } from 'app-redux/store';
import {
  BillingSearchModel,
  GetAuditLogModelResponse,
  GetBillingModel,
  GetEntityModel,
  GetEntityRoleModel,
  GetEntityTypeModel,
  GetEntityWithPersonsModel,
  UboGetReportResponse,
  UpdateEntityRegisteredTypeModel,
} from 'solstice-types';

export interface CompanyData extends GetEntityModel {
  entityType: string;
}

interface GetBillingTransactionsModel {
  dateMonth: string;
  totalValue: number;
}

const groupsApiSlice = apiPrimeSlice.injectEndpoints({
  endpoints: (builder) => ({
    primeSearch: builder.mutation<GetEntityModel[], any>({
      query: ({ searchTerm, pageNumber, pageSize, status, entityType }) => ({
        url: `/entities/prime-search`,
        method: 'POST',
        body: { searchTerm, pageNumber, pageSize, status },
        params: entityType ? { entityType } : {},
      }),
    }),

    searchProvider: builder.mutation<CompanyData[], { searchTerm: string }>({
      query: (searchTerm) => ({
        url: `/entities/provider-search`,
        method: 'POST',
        body: searchTerm,
      }),
    }),

    searchAsic: builder.mutation<UboGetReportResponse, { searchTerm: string }>({
      query: (searchTerm) => ({
        url: `/entities/asic-search`,
        method: 'POST',
        body: searchTerm,
      }),
    }),

    getAllGroups: builder.query<GetEntityModel[], void>({
      query: () => ({
        url: '/entities',
        method: 'GET',
      }),
      providesTags: ['Groups'],
    }),

    getEntityById: builder.query<GetEntityWithPersonsModel, { id: string }>({
      query: ({ id }) => ({
        url: `/entities/${id}`,
        method: 'GET',
      }),
      providesTags: ['GetEntityById'],
    }),

    getEntityRoles: builder.query<GetEntityRoleModel[], any>({
      query: ({ type } = {}) => ({
        url: `/entity-roles`,
        method: 'GET',
        params: type ? { type } : {},
      }),
      providesTags: ['EntityRoles'],
    }),

    getEntityTypes: builder.query<GetEntityTypeModel[], void>({
      query: () => ({
        url: `/entity-types`,
        method: 'GET',
      }),
    }),

    getAuditLogById: builder.query<GetAuditLogModelResponse, string>({
      query: (id) => ({
        url: `/audit-logs/${id}`,
        method: 'GET',
      }),
    }),

    getBillingData: builder.mutation<GetBillingModel[], BillingSearchModel>({
      query: ({ entityTypeId, startFrom, client, pageSize, pageNumber }) => ({
        url: '/billing',
        method: 'POST',
        body: {
          entityTypeId,
          startFrom,
          client,
          pageSize,
          pageNumber,
        },
      }),
    }),

    getBillingTransactions: builder.query<GetBillingTransactionsModel[], void>({
      query: () => ({
        url: '/billing/chart',
        method: 'GET',
      }),
    }),

    updateTrustType: builder.mutation<
      UpdateEntityRegisteredTypeModel,
      { registeredTypeId: string; entityId: string }
    >({
      query: ({ registeredTypeId, entityId }) => ({
        url: `/entities/${entityId}`,
        method: 'PUT',
        body: { registeredTypeId },
      }),
    }),

    getEntityStatusReportPdf: builder.query<{ blob: Blob; filename: string }, string>({
      query: (entityId) => ({
        url: `/entities/GetStatusReportPDF?entityId=${entityId}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (blob: Blob, meta) => {
        const response = (meta as FetchBaseQueryMeta | undefined)?.response;
        const contentDisposition = response?.headers.get('content-disposition') ?? '';
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
        const filename = utf8Match
          ? decodeURIComponent(utf8Match[1])
          : (asciiMatch?.[1] ?? '');
        return { blob, filename };
      },
    }),
  }),
});

export const {
  usePrimeSearchMutation,
  useGetAllGroupsQuery,
  useSearchProviderMutation,
  useSearchAsicMutation,
  useGetEntityByIdQuery,
  useLazyGetEntityByIdQuery,
  useGetEntityRolesQuery,
  useGetAuditLogByIdQuery,
  useUpdateTrustTypeMutation,
  useGetBillingDataMutation,
  useGetBillingTransactionsQuery,
  useGetEntityTypesQuery,
  useLazyGetEntityStatusReportPdfQuery,
} = groupsApiSlice;
